import { useState, useCallback, useMemo, ChangeEvent, FC } from 'react'
import moment from 'moment'
import { LineChart } from 'components/features/external/PublicSpendingTrend/LineChart'
import styles from 'components/features/external/PublicSpendingTrend/LineChartWrapper.styl'
import { formatUSCurrency } from 'helpers/formatHelpers'
import { useStoreDispatch } from 'helpers/reactReduxHelpers'
import { PUBLIC_TIMEFRAMES } from 'constants/publicDashboard'
import { PublicTimeframe, PublicTrendItem } from 'interfaces/publicDashboard'
import { setPublicFilterActionCreator } from 'actions/publicDashboardActions'
import { isFeatureEnabled } from 'helpers/featureHelpers'
import cn from 'classnames'


type LineChartWrapperProps = {
  items: PublicTrendItem[]
  isPreliminary: boolean
  timeframe: PublicTimeframe
}

export const LineChartWrapper: FC<LineChartWrapperProps> = (props) => {
  const { items, timeframe, isPreliminary } = props

  const lastItem = items[items.length - 1]
  const [item, setItem] = useState<PublicTrendItem>(lastItem)
  const isLastItem = item === lastItem

  const handleHighlight = useCallback<(item: PublicTrendItem | null) => void>((highlightedItem) => {
    setItem(highlightedItem || lastItem)
  }, [lastItem])

  const headerChild = useMemo(() => {
    const headerChildClassName = cn(styles.header, {
      [styles.showPreliminary]: isLastItem && isPreliminary,
    })
    return (
      <div className={ headerChildClassName }>
        <div className={ styles.title }>Week of { moment(item.date).format('MMMM Do') }</div>
        { isPreliminary && <div className={ styles.preliminary }>Preliminary</div> }
        <div className={ styles.amount }>{ formatUSCurrency(item.amount) }</div>
      </div>
    )
  }, [item, isLastItem, isPreliminary])

  const dispatch = useStoreDispatch()
  const handleTimeframeChange = useCallback<(e: ChangeEvent<HTMLSelectElement>) => void>((e) => {
    const nextTimeframe = e.target.value as PublicTimeframe
    dispatch(setPublicFilterActionCreator({ timeframe: nextTimeframe }))
  }, [dispatch])

  const isPublicSpendingTrendFilterEnabled = isFeatureEnabled('public_spending_trend_filter')

  const filterChild = useMemo(() => {
    const optionsChildren = PUBLIC_TIMEFRAMES
      .map(({ id, name }) => (
        <option key={ id } value={ id }>{ name }</option>
      ))

    return (
      <div className={ styles.filterWrapper }>
        <select
          value={ timeframe }
          className={ styles.timeframeFilter }
          onChange={ handleTimeframeChange }
        >
          { optionsChildren }
        </select>
      </div>

    )
  }, [timeframe, handleTimeframeChange])

  return (
    <div className={ styles.lineChartWrapper }>
      { headerChild }
      <div className={ styles.chart }>
        <LineChart
          items={ items }
          onHighlight={ handleHighlight }
          isPreliminary={ isPreliminary }
        />
      </div>
      { isPublicSpendingTrendFilterEnabled && filterChild }
    </div>
  )
}
